import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { IMGS, onImgError } from "@/lib/images";

const SLIDES = [
  {
    id: 1,
    tag: "Precision Voltage Regulation",
    title: "Servo Voltage Stabilizers",
    subtitle:
      "Protect your critical loads with ±1% precision regulation across single-phase and three-phase systems, air or oil cooled.",
    href: "/products/servo-voltage-stabilizer",
    image: IMGS.heroServo,
    panelImage: IMGS.servo,
  },
  {
    id: 2,
    tag: "Ultra-Isolation Technology",
    title: "Isolation Transformers",
    subtitle:
      "Galvanic separation with K-rated cores — eliminate ground loops, harmonics and common-mode noise from sensitive equipment.",
    href: "/products/isolation-transformer",
    image: IMGS.heroIsolation,
    panelImage: IMGS.isolation,
  },
  {
    id: 3,
    tag: "Phase Automation",
    title: "APSC & Automatic Phase Changer",
    subtitle:
      "Prevent motor damage from phase reversal or failure with automatic three-phase controllers — 24×7 protection.",
    href: "/products/phase-sequence-corrector",
    image: IMGS.heroApsc,
    panelImage: IMGS.apsc,
  },
];

export function HeroCarousel({ onOpenEnquiry }) {
  const [index, setIndex] = useState(0);
  const next = useCallback(
    () => setIndex((i) => (i + 1) % SLIDES.length),
    [],
  );
  const prev = () => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length);

  useEffect(() => {
    const id = setInterval(next, 6500);
    return () => clearInterval(id);
  }, [next]);

  const slide = SLIDES[index];

  return (
    <section
      className="relative bg-vortex-navy text-white overflow-hidden"
      data-testid="hero-carousel"
    >
      {/* Full-width background image */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img
            key={`bg-${slide.id}`}
            src={slide.image}
            onError={onImgError}
            alt=""
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-vortex-navy-deep via-vortex-navy/85 to-vortex-navy/50" />
      </div>
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

      <div className="container-wide relative z-10 min-h-[86vh] flex items-center py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          <div className="max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              >
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-vortex-blue/40 bg-vortex-blue/10 text-vortex-blue text-xs font-bold uppercase tracking-widest mb-6">
                  <Zap className="w-3.5 h-3.5" strokeWidth={2.5} />
                  {slide.tag}
                </div>
                <h1 className="font-heading font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter mb-6 text-balance">
                  {slide.title}
                </h1>
                <p className="text-lg md:text-xl text-white/75 leading-relaxed max-w-2xl mb-8 text-balance">
                  {slide.subtitle}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={onOpenEnquiry}
                    className="rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white px-7 h-12 font-semibold shadow-lg hover:shadow-orange-500/25 transition-all"
                    data-testid="hero-quote-btn"
                  >
                    Contact Us
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} />
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="rounded-full bg-transparent border-2 border-white/25 text-white hover:bg-white hover:text-vortex-navy px-7 h-12 font-semibold transition-colors"
                    data-testid="hero-learn-btn"
                  >
                    <Link to={slide.href}>Learn More</Link>
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right-side image placeholder panel */}
          <div className="hidden lg:flex justify-end">
            <AnimatePresence mode="wait">
              <motion.div
                key={`panel-${slide.id}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.7, ease: "easeOut" }}
                className="relative w-full max-w-md aspect-[4/3] rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-sm shadow-2xl"
                data-testid={`hero-panel-${slide.id}`}
              >
                <img
                  src={slide.panelImage}
                  onError={onImgError}
                  alt={slide.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid={`hero-panel-img-${slide.id}`}
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Slide controls */}
      <div className="absolute bottom-8 md:bottom-10 right-6 md:right-10 z-20 flex items-center gap-4">
        <button
          onClick={prev}
          aria-label="Previous slide"
          className="w-11 h-11 rounded-full border border-white/30 hover:bg-vortex-blue hover:border-vortex-blue text-white flex items-center justify-center transition-colors"
          data-testid="hero-prev-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1 rounded-full transition-all ${
                i === index
                  ? "w-8 bg-vortex-blue"
                  : "w-4 bg-white/40 hover:bg-white/70"
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
        <button
          onClick={next}
          aria-label="Next slide"
          className="w-11 h-11 rounded-full border border-white/30 hover:bg-vortex-blue hover:border-vortex-blue text-white flex items-center justify-center transition-colors"
          data-testid="hero-next-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Slide counter */}
      <div className="absolute bottom-10 left-6 md:left-10 z-20 hidden sm:block">
        <div className="font-heading font-bold text-white/90">
          <span className="text-3xl">0{index + 1}</span>
          <span className="text-white/40 text-lg"> / 0{SLIDES.length}</span>
        </div>
      </div>
    </section>
  );
}
